import * as React from "react";
import { useHubx, useHubXSubscribe } from "../../HubX";
import CustomerModel from "../../core/models/Customer";
import useConstructor from "../../core/hooks/useConstructor";
import useInfiniteScroll from "react-infinite-scroll-hook";
import {
  FETCH_USER_FAVORITE_INSTALLATIONS,
  ADD_OR_REMOVE_FAVORITE_INSTALLATION,
  FILTER_ALL_INSTALLATIONS_NEW,
} from "../../Fedx";
import HomeGroupAccordion from "../../components/HomeGroupAccordion";
import {
  Grid,
  Box,
  makeStyles,
  createStyles,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import { IInstallation } from "../../core/contracts/IInstallation";
import { Pagination } from "../../core/contracts/Pagination";
import { isArrayValid } from "../../helpers/utility";
import { UserFavoriteInstallations } from "../../core/contracts/UserFavoriteInstallations";
import FavoriteInstallation from "../../core/models/FavoriteInstallation";
import AllInstallationsFilter from "./AllInstallationsFilter";
import {
  EVENT_ALL_INSTALLATIONS_ALREADY_LOADED,
  EVENT_ALL_INSTALLATIONS_PAGE_FILTER,
  EVENT_HOME_FILTER_ORDER_CHANGE,
  EVENT_HOME_FILTER_ORDER_NAME_CHANGE,
} from "../../Events";
import AllInstallationsFilterType from "../../core/models/AllInstallationsFilter";
import { uniq } from "lodash";
import { OrderOptions } from "../../core/contracts/APIInstallationFilter";
import InstallationModel from "../../core/models/Installation";
import { calculateLevelPercentage } from "../../helpers/installationTankHelpers";
import { LocalCache } from "../../helpers/cache";

export type CustomerAccordionType = { [id: string]: boolean } | null;

const DEFAULT_NUMBER_OF_GROUP_INSTALLATIONS = 2;
const DEFAULT_STARTING_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;
const emptyInstallations = new Array(DEFAULT_NUMBER_OF_GROUP_INSTALLATIONS).fill({});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    allInstallationsGrid: {
      paddingBottom: theme.spacing(2),
    },

    favorites: {
      marginBottom: "20px",
      maxHeight: "30vh",
      overflow: "auto",
    },

    notFound: {
      width: "100%",
      textAlign: "center",
    },
  })
);

function AllInstallations() {
  const theme = useTheme();
  const isMobileResolution = useMediaQuery(theme.breakpoints.down("xs"));

  const [customer, SetCustomer] = React.useState<CustomerModel>();
  const [favorites, setFavorites] = React.useState<CustomerModel>();
  const [
    favoriteInstallations,
    setFavoriteInstallations,
  ] = React.useState<UserFavoriteInstallations>();
  const [installationsOnScreen, setInstallationsOnScreen] = React.useState<
    IInstallation[] | undefined
  >([]);
  const [isLoading, setLoading] = React.useState(false);

  const defaultOrderOptions = {
    column: undefined,
    order: false,
  } as OrderOptions;

  const [orderOptions, setOrderOptions] = React.useState<Record<string, OrderOptions>>();

  const [
    installationFilters,
    setInstallationFilters,
  ] = React.useState<AllInstallationsFilterType>();

  const [notFound, setNotFound] = React.useState<boolean>(false);

  const [hasNextPage, setHasNextPage] = React.useState(true);
  const [pagination, setPagination] = React.useState<Pagination>({
    CurrentPage: DEFAULT_STARTING_PAGE,
    TotalPages: 0,
    PageSize: DEFAULT_PAGE_SIZE,
    TotalCount: 0,
  });

  const classes = useStyles();
  const hub = useHubx();
  const toastr = useSnackbar();

  const localCache = new LocalCache();

  const CACHE_HOLDER_NAME = "allInstallationsFilter";

  const getFilterFromCache = () => {
    const tempFilter = localCache.get<any>(CACHE_HOLDER_NAME);
    return tempFilter;
  };

  useConstructor(async () => {
    setLoading(true);
    try {
      const firstPageConfig = {
        CurrentPage: DEFAULT_STARTING_PAGE,
        TotalPages: 0,
        PageSize: DEFAULT_PAGE_SIZE,
        TotalCount: 0,
      };

      const savedFilter = await getFilterFromCache();
      const auxSavedFilter = savedFilter
        ? {
            installation_product: savedFilter.selectedProduct,
            installation_vendor: savedFilter.selectedVendor,
            installation_uf: savedFilter.selectedUF,
            installation_ldc: savedFilter.selectedCDL,
            installation_market: savedFilter.selectedMarket,
            installation_name_id: savedFilter.nameOrId,
            installation_alert: savedFilter.selectedStatus,
            installation_trip_status: savedFilter.selectedTripStatus,
          }
        : {};

      const filters = {
        ...auxSavedFilter,
        offset: firstPageConfig.CurrentPage * firstPageConfig.PageSize,
        return_ids: false,
        installation_has_rtu: 1,
      } as AllInstallationsFilterType;

      const installationFiltered: IInstallation[] | undefined = await hub.request(
        FILTER_ALL_INSTALLATIONS_NEW,
        filters
      );

      setInstallationFilters(filters);
      const favoriteUserInstallations: UserFavoriteInstallations = await hub.request(
        FETCH_USER_FAVORITE_INSTALLATIONS
      );
      setFavoriteInstallations(favoriteUserInstallations);

      setPagination(firstPageConfig);

      const installationsFirstPage: IInstallation[] | undefined = installationFiltered?.map(
        (installation) => {
          if (
            favoriteUserInstallations?.installationsIds.find(
              (installationId) => installationId === installation.installation_id
            )
          ) {
            return { ...installation, favorite: true };
          } else {
            return { ...installation, favorite: false };
          }
        }
      );

      const customerData: CustomerModel = {
        customer_id: "0",
        customer_name: "Todas as instalações",
        installations: installationsFirstPage,
      };

      setInstallationsOnScreen(installationsFirstPage);
      SetCustomer(customerData);

      if (isArrayValid(favoriteUserInstallations?.installationsIds)) {
        const filtersFavorite = {
          offset: firstPageConfig.CurrentPage * firstPageConfig.PageSize,
          return_ids: false,
          installation_ids: favoriteUserInstallations?.installationsIds.map((e) => {
            return parseInt(e);
          }),
          installation_has_rtu: 1,
          page_size: favoriteUserInstallations?.installationsIds?.length,
        } as AllInstallationsFilterType;
        const installationsFavorites: IInstallation[] = await hub.request(
          FILTER_ALL_INSTALLATIONS_NEW,
          filtersFavorite
        );

        const favoritesData = {
          customer_id: "0",
          customer_name: "Favoritos",
          installations: installationsFavorites?.map((x) => {
            return { ...x, favorite: true };
          }),
        };

        setFavorites(favoritesData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "AllInstallations → FETCH_INSTALLATIONS_ALL_IDS, FILTER_ALL_INSTALLATIONS_NEW → error",
        error
      );
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter os dados das instalações. Tente novamente mais tarde!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  });

  const ascending = (a: InstallationModel, b: InstallationModel, ref: any): any => {
    switch (ref) {
      case "product_code":
        return a.product_code && b.product_code && (a.product_code > b.product_code ? 1 : -1);
      case "name":
        return a.name && b.name && (a.name > b.name ? 1 : -1);
      case "installation_id":
        return a.installation_id > b.installation_id ? 1 : -1;
      case "tank_level_su":
        return calculateLevelPercentage(a.installation_level, a.inventory_capacity_su) >
          calculateLevelPercentage(b.installation_level, b.inventory_capacity_su)
          ? 1
          : -1;
      case "installation_level":
        return (
          a.installation_level &&
          b.installation_level &&
          (a.installation_level > b.installation_level ? 1 : -1)
        );
      case "autonomy_hours_to_outage":
        return (
          a.autonomy_hours_to_outage &&
          b.autonomy_hours_to_outage &&
          (a.autonomy_hours_to_outage > b.autonomy_hours_to_outage ? 1 : -1)
        );
      case "tank_pressure":
        return a.tanks &&
          a.tanks?.length == 1 &&
          b?.tanks &&
          b?.tanks?.length == 1 &&
          a?.tanks[0] &&
          b?.tanks[0] &&
          a?.tanks[0].tank_pressure &&
          b?.tanks[0].tank_pressure
          ? a?.tanks[0].tank_pressure > b?.tanks[0].tank_pressure
            ? 1
            : -1
          : -1;
      case "inventory_capacity_su":
        return (
          a.inventory_capacity_su &&
          b.inventory_capacity_su &&
          (a.inventory_capacity_su > b.inventory_capacity_su ? 1 : -1)
        );
      case "installation_level_status":
        return (
          a.installation_level_status &&
          b.installation_level_status &&
          (a.installation_level_status > b.installation_level_status ? 1 : -1)
        );
      case "last_rtu_reading_date":
        return (
          a.last_rtu_reading_date &&
          b.last_rtu_reading_date &&
          (a.last_rtu_reading_date > b.last_rtu_reading_date ? 1 : -1)
        );
      case "number_of_tanks":
        return (
          a.number_of_tanks && b.number_of_tanks && (a.number_of_tanks > b.number_of_tanks ? 1 : -1)
        );

      default:
        return 1;
        break;
    }
  };

  const descending = (a: InstallationModel, b: InstallationModel, ref: any): any => {
    switch (ref) {
      case "product_code":
        return a.product_code && b.product_code && (a.product_code > b.product_code ? -1 : 1);
      case "name":
        return a.name && b.name && (a.name > b.name ? -1 : 1);
      case "installation_id":
        return a.installation_id > b.installation_id ? -1 : 1;
      case "tank_level_su":
        return calculateLevelPercentage(a.installation_level, a.inventory_capacity_su) >
          calculateLevelPercentage(b.installation_level, b.inventory_capacity_su)
          ? -1
          : 1;
      case "installation_level":
        return (
          a.installation_level &&
          b.installation_level &&
          (a.installation_level > b.installation_level ? -1 : 1)
        );
      case "autonomy_hours_to_outage":
        return (
          a.autonomy_hours_to_outage &&
          b.autonomy_hours_to_outage &&
          (a.autonomy_hours_to_outage > b.autonomy_hours_to_outage ? -1 : 1)
        );
      case "tank_pressure":
        return a.tanks &&
          a.tanks?.length == 1 &&
          b?.tanks &&
          b?.tanks?.length == 1 &&
          a?.tanks[0] &&
          b?.tanks[0] &&
          a?.tanks[0].tank_pressure &&
          b?.tanks[0].tank_pressure
          ? a?.tanks[0].tank_pressure > b?.tanks[0].tank_pressure
            ? -1
            : 1
          : 1;
      case "inventory_capacity_su":
        return (
          a.inventory_capacity_su &&
          b.inventory_capacity_su &&
          (a.inventory_capacity_su > b.inventory_capacity_su ? -1 : 1)
        );
      case "installation_level_status":
        return (
          a.installation_level_status &&
          b.installation_level_status &&
          (a.installation_level_status > b.installation_level_status ? -1 : 1)
        );
      case "last_rtu_reading_date":
        return (
          a.last_rtu_reading_date &&
          b.last_rtu_reading_date &&
          (a.last_rtu_reading_date > b.last_rtu_reading_date ? -1 : 1)
        );
      case "number_of_tanks":
        return (
          a.number_of_tanks && b.number_of_tanks && (a.number_of_tanks > b.number_of_tanks ? -1 : 1)
        );

      default:
        return 1;
        break;
    }
  };

  const sort = (a: any, b: any, ref: any, isReverse: boolean) => {
    return isReverse ? descending(a, b, ref) : ascending(a, b, ref);
  };

  const onChangeOrder = async (
    customerId: string | undefined,
    idx: string,
    isReverse: boolean | undefined
  ) => {
    const resetCustomerData: CustomerModel = {
      customer_id: "0",
      customer_name: "Todas as instalações",
      installations: [],
    };
    SetCustomer(resetCustomerData);
    setLoading(true);
    try {
      const type = isReverse ? "desc" : "asc";
      if (idx != "") {
        if (customerId) {
          const prevOrder = orderOptions ? orderOptions : ({} as Record<string, OrderOptions>);
          prevOrder[customerId] = { column: idx, order: isReverse };
          setOrderOptions(prevOrder);
        }
        const filter = {
          ...installationFilters,
          order: { [idx]: type },
          installation_has_rtu: 1,
        } as AllInstallationsFilterType;

        const resetPageConfig = {
          CurrentPage: DEFAULT_STARTING_PAGE,
          TotalPages: 0,
          PageSize: DEFAULT_PAGE_SIZE,
          TotalCount: 0,
        };

        const auxFilters = {
          ...filter,
          return_ids: false,
          offset: resetPageConfig.CurrentPage * resetPageConfig.PageSize,
          installation_has_rtu: 1,
        };

        setInstallationFilters(auxFilters);

        let filteredInstall: any[] = await hub.request(FILTER_ALL_INSTALLATIONS_NEW, auxFilters);

        filteredInstall = filteredInstall.sort((a: IInstallation, b: IInstallation) =>
          sort(a, b, idx, !!isReverse)
        );

        setPagination(resetPageConfig);

        const resultWithFavoriteProperty: IInstallation[] = filteredInstall.map((installation) => {
          if (
            favoriteInstallations?.installationsIds?.find(
              (installationId) => installationId === installation.installation_id
            )
          ) {
            return { ...installation, favorite: true };
          } else {
            return { ...installation, favorite: false };
          }
        });

        setInstallationsOnScreen(resultWithFavoriteProperty);

        const customerData: CustomerModel = {
          customer_id: "0",
          customer_name: "Todas as instalações",
          installations: resultWithFavoriteProperty,
        };

        SetCustomer(customerData);
        hub.notify(EVENT_HOME_FILTER_ORDER_NAME_CHANGE, idx);
        hub.notify(EVENT_HOME_FILTER_ORDER_CHANGE, isReverse);
      }
    } catch (error) {
      console.error("AllInstallations → FILTER_ALL_INSTALLATIONS_NEW → error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter os dados das instalações. Tente novamente mais tarde!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onLoadMore = async () => {
    setLoading(true);

    try {
      const page = { ...pagination, CurrentPage: pagination?.CurrentPage + 1 };
      const auxFilters = {
        ...installationFilters,
        offset: page.CurrentPage * page.PageSize,
        installation_has_rtu: 1,
      };
      let result: IInstallation[] = await hub.request(FILTER_ALL_INSTALLATIONS_NEW, auxFilters);

      if (orderOptions && customer && orderOptions[customer.customer_id]) {
        const { column, order } = orderOptions[customer.customer_id];
        result = result.sort((a: IInstallation, b: IInstallation) => sort(a, b, column, !!order));
      }

      let resultWithFavoriteProperty: IInstallation[] = [];

      if (result && installationsOnScreen && isArrayValid(installationsOnScreen)) {
        resultWithFavoriteProperty = result.map((installation) => {
          if (
            favoriteInstallations?.installationsIds?.find(
              (installationId) => installationId === installation.installation_id
            )
          ) {
            return { ...installation, favorite: true };
          } else {
            return { ...installation, favorite: false };
          }
        });

        const installationsResult = [...installationsOnScreen, ...resultWithFavoriteProperty];

        setInstallationsOnScreen(installationsResult);

        const customerData: CustomerModel = {
          customer_id: "0",
          customer_name: "Todas as instalações",
          installations: installationsResult,
        };

        SetCustomer(customerData);
        setInstallationFilters(auxFilters);
        setPagination(page);
      }

      setHasNextPage(!!result.length);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("AllInstallations → FILTER_ALL_INSTALLATIONS_NEW → error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter os dados das instalações. Tente novamente mais tarde!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async (params: { filters: AllInstallationsFilterType; loading: boolean }) => {
    const filtersSearch = params.filters;
    const need2Loading = params.loading;
    need2Loading && setLoading(true);
    hub.notify(EVENT_ALL_INSTALLATIONS_ALREADY_LOADED);
    try {
      const resetPageConfig = {
        CurrentPage: DEFAULT_STARTING_PAGE,
        TotalPages: 0,
        PageSize: DEFAULT_PAGE_SIZE,
        TotalCount: 0,
      };

      const auxFilters = {
        ...filtersSearch,
        return_ids: false,
        offset: resetPageConfig.CurrentPage * resetPageConfig.PageSize,
        installation_has_rtu: 1,
      };

      let filteredInstallations: IInstallation[] = await hub.request(
        FILTER_ALL_INSTALLATIONS_NEW,
        auxFilters
      );

      if (orderOptions && customer && orderOptions[customer.customer_id]) {
        const { column, order } = orderOptions[customer.customer_id];
        filteredInstallations = filteredInstallations.sort((a: IInstallation, b: IInstallation) =>
          sort(a, b, column, !!order)
        );
      }

      setInstallationFilters(auxFilters);

      if (filteredInstallations) {
        setNotFound(false);

        setPagination(resetPageConfig);

        const resultWithFavoriteProperty: IInstallation[] = filteredInstallations.map(
          (installation) => {
            if (
              favoriteInstallations?.installationsIds?.find(
                (installationId) => installationId === installation.installation_id
              )
            ) {
              return { ...installation, favorite: true };
            } else {
              return { ...installation, favorite: false };
            }
          }
        );

        setInstallationsOnScreen(resultWithFavoriteProperty);

        const customerData: CustomerModel = {
          customer_id: "0",
          customer_name: "Todas as instalações",
          installations: resultWithFavoriteProperty,
        };

        SetCustomer(customerData);

        setHasNextPage(!!resultWithFavoriteProperty.length);
        hub.notify(EVENT_ALL_INSTALLATIONS_ALREADY_LOADED);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("AllInstallations → FILTER_ALL_INSTALLATIONS_NEW → error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter os dados das instalações. Tente novamente mais tarde!",
        { variant: "error" }
      );
    } finally {
      need2Loading && setLoading(false);
    }
  };

  const handleFavorite = async (id: string, favoriteOption: boolean) => {
    const favoriteInstallation: FavoriteInstallation = {
      favorite: favoriteOption,
      installationId: id,
    };

    try {
      await hub.request(ADD_OR_REMOVE_FAVORITE_INSTALLATION, favoriteInstallation);

      toastr.enqueueSnackbar(
        favoriteOption
          ? `Instalação ${id} adicionada aos favoritos!`
          : `Instalação ${id} removida dos favoritos!`,
        {
          variant: "success",
        }
      );

      let favoritesData = favorites;

      if (favoriteOption) {
        const targetInstallation = installationsOnScreen?.filter(
          (installation) => installation.installation_id === id
        );

        favoritesData = {
          customer_id: "0",
          customer_name: "Favoritos",
          installations:
            targetInstallation &&
            (favorites?.installations
              ? [...favorites.installations, ...targetInstallation]
              : targetInstallation
            )?.map((x) => {
              return { ...x, favorite: true };
            }),
        };

        setFavorites(favoritesData);
      } else {
        favoritesData = {
          customer_id: "0",
          customer_name: "Favoritos",
          installations: favorites?.installations
            ?.filter((installation) => installation.installation_id !== id)
            ?.map((x) => {
              return { ...x, favorite: true };
            }),
        };
        setFavorites(favoritesData);
      }

      if (customer && isArrayValid(customer?.installations)) {
        const favoritesIds = favoritesData?.installations?.map((x) => x.installation_id);

        const installationsUpdated = customer?.installations?.map((installation) => {
          if (favoritesIds?.includes(installation.installation_id)) {
            return { ...installation, favorite: true };
          } else {
            return { ...installation, favorite: false };
          }
        });

        const customerData: CustomerModel = {
          customer_id: "0",
          customer_name: "Todas as instalações",
          installations: installationsUpdated,
        };

        SetCustomer(customerData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("AllInstallations -> fetchInstallationGroups → error", error);
      if (favoriteOption) {
        toastr.enqueueSnackbar(
          `Ops! Ocorreu um erro ao tentar adicionar a instalação ${id} aos favoritos! Tente novamente mais tarde.`,
          { variant: "error" }
        );
      } else {
        toastr.enqueueSnackbar(
          `Ops! Ocorreu um erro ao tentar remover a instalação ${id} dos favoritos! Tente novamente mais tarde.`,
          { variant: "error" }
        );
      }
    }
  };

  useHubXSubscribe(EVENT_ALL_INSTALLATIONS_PAGE_FILTER, doSearch);

  const infiniteRef = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasNextPage,
    onLoadMore,
    scrollContainer: "window",
  });

  return (
    <>
      <AllInstallationsFilter />

      <Box mt={3}>
        {favorites && (
          <Grid className={classes.favorites}>
            <HomeGroupAccordion
              startOpen
              favoriteGroup
              hasFavorites
              onChangeOrder={onChangeOrder}
              orderOptions={orderOptions ? orderOptions["favorite"] : defaultOrderOptions}
              customer={favorites}
              key={favorites.customer_id}
              selectedView={isMobileResolution ? "mobile-card" : "table"}
              handleFavorite={handleFavorite}
            />
          </Grid>
        )}

        {customer && (
          <Grid
            className={classes.allInstallationsGrid}
            ref={infiniteRef as React.RefObject<HTMLTableSectionElement>}
          >
            <HomeGroupAccordion
              orderOptions={orderOptions ? orderOptions[customer.customer_id] : defaultOrderOptions}
              startOpen
              hasFavorites
              alwaysExpanded
              onChangeOrder={onChangeOrder}
              key={customer.customer_id}
              customer={customer}
              selectedView={isMobileResolution ? "mobile-card" : "table"}
              handleFavorite={handleFavorite}
            />
          </Grid>
        )}

        {isLoading &&
          emptyInstallations.map((_, idx) => (
            <Skeleton height={75} key={`installation_group_skeleton_${idx}`} />
          ))}

        {notFound && (
          <div className={classes.notFound}>
            <Typography>Não existem resultados para esta pesquisa.</Typography>
          </div>
        )}
      </Box>
    </>
  );
}

export default AllInstallations;
